import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'

import { readLoginPreferences, rememberLoginLocale } from '@/shared/preferences/loginPreferences'

import type { MessageId } from './catalogue.en'
import {
  DEFAULT_LOCALE,
  type Direction,
  type Locale,
  type Translate,
  directionOf,
  negotiateLocale,
  translate,
} from './locale'

/**
 * Where the active locale came from. Browser negotiation and the remembered
 * public-login hint both yield to the session; an authenticated update's
 * server answer is the last word.
 */
type LocaleSource = 'negotiated' | 'login' | 'session' | 'user'

export interface I18nValue {
  locale: Locale
  direction: Direction
  /** A public-login choice remembered by this browser until an account is known. */
  setLoginLocale: (locale: Locale) => void
  /** Restore this browser's public-login hint after an account leaves. */
  applyLoginLocale: () => void
  /**
   * Adopt the locale `/app-context` persists for the workspace. Applies only
   * while the locale is still a browser guess or public-login hint, so a
   * session refetch can never undo a successful authenticated switch.
   */
  applySessionLocale: (locale: Locale, direction: Direction) => void
  /** Adopt the exact locale pair returned by a successful account update. */
  applyPersistedLocale: (locale: Locale, direction: Direction) => void
  t: Translate
}

const I18nContext = createContext<I18nValue | null>(null)

export interface I18nProviderProps {
  children: ReactNode
  /** Test seam and SSR-free entry point; production negotiates from the browser. */
  initialLocale?: Locale
}

interface LocaleState {
  language: Locale
  direction: Direction
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [initial] = useState(() => initialLocaleResolution(initialLocale))
  const [localeState, setLocaleState] = useState<LocaleState>(initial.state)

  // `initialLocale` stands in for browser negotiation rather than for a choice.
  // A remembered login value is still only a browser hint, so the authenticated
  // account may override either one.
  const sourceRef = useRef<LocaleSource>(initial.source)

  const setLoginLocale = useCallback((next: Locale) => {
    sourceRef.current = 'login'
    rememberLoginLocale(next)
    setLocaleState(localLocaleState(next))
  }, [])

  const applyLoginLocale = useCallback(() => {
    const remembered = readLoginPreferences().locale
    sourceRef.current = remembered ? 'login' : 'negotiated'
    setLocaleState(
      localLocaleState(
        remembered ?? initialLocale ?? negotiateLocale(readNavigatorLanguages()),
      ),
    )
  }, [initialLocale])

  const applySessionLocale = useCallback((language: Locale, direction: Direction) => {
    if (sourceRef.current !== 'negotiated' && sourceRef.current !== 'login') {
      return
    }
    sourceRef.current = 'session'
    setLocaleState({ language, direction })
  }, [])

  const applyPersistedLocale = useCallback((language: Locale, direction: Direction) => {
    sourceRef.current = 'user'
    setLocaleState({ language, direction })
  }, [])

  // Direction and language belong on the document element, not on a wrapper
  // div: `dir=rtl` has to reach scrollbars, form controls and the browser's own
  // text selection, and assistive technology reads `lang` from the root.
  useLayoutEffect(() => {
    const root = document.documentElement
    root.lang = localeState.language
    root.dir = localeState.direction
  }, [localeState])

  const t = useCallback(
    (id: MessageId, params?: Readonly<Record<string, string | number>>) =>
      translate(localeState.language, id, params),
    [localeState.language],
  )

  const value = useMemo<I18nValue>(
    () => ({
      locale: localeState.language,
      direction: localeState.direction,
      setLoginLocale,
      applyLoginLocale,
      applySessionLocale,
      applyPersistedLocale,
      t,
    }),
    [
      localeState,
      setLoginLocale,
      applyLoginLocale,
      applySessionLocale,
      applyPersistedLocale,
      t,
    ],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) {
    throw new Error('useI18n must be used inside I18nProvider')
  }
  return value
}

/** Convenience hook for the common case of only needing the translate function. */
export function useTranslate(): I18nValue['t'] {
  return useI18n().t
}

function readNavigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') {
    return [DEFAULT_LOCALE]
  }
  return navigator.languages ?? [navigator.language]
}

function localLocaleState(language: Locale): LocaleState {
  return { language, direction: directionOf(language) }
}

function initialLocaleResolution(initialLocale: Locale | undefined): {
  state: LocaleState
  source: LocaleSource
} {
  const remembered = readLoginPreferences().locale
  return {
    state: localLocaleState(
      remembered ?? initialLocale ?? negotiateLocale(readNavigatorLanguages()),
    ),
    source: remembered ? 'login' : 'negotiated',
  }
}
